import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Typography, Card, message, Divider } from 'antd';
import {
  MailOutlined, LockOutlined, LoginOutlined,
  MobileOutlined, SafetyOutlined, GoogleOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../store/AuthContext';
import { useLanguage } from '../store/LanguageContext';
import { detectSiteMode } from '../utils/site';
import SliderCaptcha from '../components/SliderCaptcha';

const { Title, Text } = Typography;

// ==================== 手机验证码登录表单 ====================
const PhoneLoginForm: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { sendSmsCode, loginByPhone } = useContext(AuthContext);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [captchaPassed, setCaptchaPassed] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const startCountdown = useCallback(() => {
    setCountdown(60);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSendCode = async (phone: string) => {
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      message.error(t('auth.phoneInvalid'));
      return;
    }
    if (!captchaPassed) {
      message.warning('请先完成滑动验证');
      return;
    }
    try {
      await sendSmsCode(phone);
      message.success(t('auth.codeSent'));
      startCountdown();
    } catch (err: any) {
      message.error(err.response?.data?.error || t('auth.codeSendFailed'));
    }
  };

  const handleSubmit = async (values: { phone: string; code: string }) => {
    try {
      setLoading(true);
      await loginByPhone(values.phone, values.code);
      message.success(t('auth.loginSuccess'));
      onSuccess();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form name="phoneLogin" onFinish={handleSubmit} size="large" layout="vertical">
      <Form.Item
        name="phone"
        label={t('auth.phoneLabel')}
        rules={[
          { required: true, message: t('auth.phoneRequired') },
          { pattern: /^1[3-9]\d{9}$/, message: t('auth.phoneInvalid') },
        ]}
      >
        <Input prefix={<MobileOutlined />} placeholder={t('auth.phonePlaceholder')} maxLength={11} />
      </Form.Item>

      {/* 滑动验证 */}
      <Form.Item label="安全验证" style={{ marginBottom: 16 }}>
        <SliderCaptcha onSuccess={() => setCaptchaPassed(true)} />
      </Form.Item>

      <Form.Item label={t('auth.codeLabel')} required>
        <div style={{ display: 'flex', gap: 8 }}>
          <Form.Item
            name="code"
            noStyle
            rules={[
              { required: true, message: t('auth.codeRequired') },
              { len: 6, message: t('auth.codeLength') },
            ]}
          >
            <Input prefix={<SafetyOutlined />} placeholder={t('auth.codePlaceholder')} maxLength={6} style={{ flex: 1 }} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.phone !== cur.phone}>
            {({ getFieldValue }) => (
              <Button
                disabled={countdown > 0 || !captchaPassed}
                onClick={() => handleSendCode(getFieldValue('phone'))}
                style={{ minWidth: 120 }}
              >
                {countdown > 0 ? `${countdown}s` : t('auth.sendCode')}
              </Button>
            )}
          </Form.Item>
        </div>
      </Form.Item>

      <Form.Item style={{ marginBottom: 16 }}>
        <Button type="primary" htmlType="submit" block loading={loading}>
          {t('nav.login')}
        </Button>
      </Form.Item>
    </Form>
  );
};

// ==================== 邮箱密码登录表单 ====================
const EmailLoginForm: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { login } = useContext(AuthContext);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: { email: string; password: string }) => {
    try {
      setLoading(true);
      await login(values.email, values.password);
      message.success(t('auth.loginSuccess'));
      onSuccess();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form name="emailLogin" onFinish={handleSubmit} size="large" layout="vertical">
      <Form.Item
        name="email"
        label={t('auth.emailLabel')}
        rules={[
          { required: true, message: t('auth.emailRequired') },
          { type: 'email', message: t('auth.emailInvalid') },
        ]}
      >
        <Input prefix={<MailOutlined />} placeholder="your@email.com" />
      </Form.Item>

      <Form.Item
        name="password"
        label={t('auth.passwordLabel')}
        rules={[
          { required: true, message: t('auth.passwordRequired') },
          { min: 6, message: t('auth.passwordMin') },
        ]}
      >
        <Input.Password prefix={<LockOutlined />} placeholder={t('auth.passwordPlaceholder')} />
      </Form.Item>

      <Form.Item style={{ marginBottom: 16 }}>
        <Button type="primary" htmlType="submit" block loading={loading}>
          {t('nav.login')}
        </Button>
      </Form.Item>
    </Form>
  );
};

// ==================== Google 登录按钮 ====================
const GoogleLoginButton: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { loginByGoogle } = useContext(AuthContext);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 动态加载 Google Identity Services SDK
    const existingScript = document.getElementById('google-gsi');
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'google-gsi';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.body.appendChild(script);
    } else {
      initializeGoogle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeGoogle = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !(window as any).google) return;

    (window as any).google.accounts.id.initialize({
      client_id: clientId,
      callback: handleGoogleResponse,
    });

    if (googleBtnRef.current) {
      (window as any).google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline',
        size: 'large',
        width: '100%',
        text: 'signin_with',
      });
    }
  };

  const handleGoogleResponse = async (response: any) => {
    if (!response.credential) return;
    try {
      setLoading(true);
      await loginByGoogle(response.credential);
      message.success(t('auth.loginSuccess'));
      onSuccess();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('auth.googleLoginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div ref={googleBtnRef} style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }} />
      {loading && <div style={{ textAlign: 'center', marginTop: 8 }}><Text type="secondary">{t('auth.loggingIn')}</Text></div>}
      {/* 备用按钮（GSI 未加载时） */}
      {!process.env.REACT_APP_GOOGLE_CLIENT_ID && (
        <Button
          icon={<GoogleOutlined />}
          size="large"
          block
          style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          disabled
        >
          {t('auth.googleLogin')} ({t('auth.googleNotConfigured')})
        </Button>
      )}
    </div>
  );
};

// ==================== 主登录页面 ====================
const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const usePhoneLogin = detectSiteMode() === 'CN';

  const handleLoginSuccess = () => {
    navigate('/');
  };

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '40px 16px',
      }}
    >
      <Card
        style={{
          width: 440,
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 8 }}>
            <LoginOutlined /> {t('auth.welcomeBack')}
          </Title>
          <Text type="secondary">{t('auth.loginDesc')}</Text>
        </div>

        {usePhoneLogin ? (
          <>
            <PhoneLoginForm onSuccess={handleLoginSuccess} />
            <div style={{ textAlign: 'center', marginTop: -8 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t('auth.autoRegisterHint')}
              </Text>
            </div>
          </>
        ) : (
          <>
            <GoogleLoginButton onSuccess={handleLoginSuccess} />

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t('auth.autoRegisterHint')}
              </Text>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default LoginPage;
